//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import {
  RECOMMENDED_IG_STORY_ASPECT_RATIO,
  STORY_MAX_IMAGE_SIZE,
  STORY_MAX_VIDEO_DURATION_MILLIS,
  STORY_MAX_VIDEO_SIZE,
  STORY_MIN_VIDEO_DURATION_MILLIS,
} from '@instagram-account/post-details/postDetailsUIConstants';
import { InstagramPostDetailsValidationError } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import { IInstagramPostDetailsStoryClip } from '@instagram-account/redux/story-group-post-details/instagramStoryGroupPostDetailsModel';
import Cuid from '@models/Cuid';
import { INetworkItem, isGif, isImage, isVideo } from '@models/INetworkItem';
import {
  IInstagramStoryGroupClipCustomization,
  InstagramStoryGroupClipCustomizationType,
} from '@models/IScheduledInstagramStoryGroup';
import { InstagramPublishingStrategy } from '@models/InstagramPublishingStrategy';
import { isDefined } from '@utils/filterPredicates';
import { isValidUrlWithProtocol } from '@utils/urlValidation';

export const validateStoryClip = (
  storyClip: IInstagramPostDetailsStoryClip,
  publishingStrategy: InstagramPublishingStrategy,
  associatedNetworkItem?: INetworkItem,
): PojoSet<InstagramPostDetailsValidationError> => {
  let validationErrors = PojoSet.empty<InstagramPostDetailsValidationError>();
  if (!storyClip.cuid) {
    validationErrors = PojoSet.add(
      validationErrors,
      InstagramPostDetailsValidationError.NoMediaStoryClip,
    );
  } else if (associatedNetworkItem && isGif(associatedNetworkItem)) {
    validationErrors = PojoSet.add(
      validationErrors,
      InstagramPostDetailsValidationError.Gif,
    );
  }

  if (isDefined(associatedNetworkItem) && isVideo(associatedNetworkItem)) {
    const videoDuration = associatedNetworkItem.mediaMetadata?.durationMillis;
    const videoSize = associatedNetworkItem.mediaMetadata?.fileSize;

    if (
      !isDefined(videoDuration) ||
      !isValidIgStoryDuration(videoDuration) ||
      (isDefined(videoSize) && !isValidIgStoryVideoSize(videoSize))
    ) {
      validationErrors = PojoSet.add(
        validationErrors,
        InstagramPostDetailsValidationError.InvalidStoryVideo,
      );
    }
  } else if (
    isDefined(associatedNetworkItem) &&
    isImage(associatedNetworkItem)
  ) {
    const imageSize = associatedNetworkItem.mediaMetadata?.fileSize;

    if (isDefined(imageSize) && !isValidIgStoryImageSize(imageSize)) {
      validationErrors = PojoSet.add(
        validationErrors,
        InstagramPostDetailsValidationError.InvalidIgImageSize,
      );
    }
  }
  if (publishingStrategy == InstagramPublishingStrategy.Manual) {
    const swipeUpLink = storyClip.fullSwipeUpLink;

    if (swipeUpLink && !isValidUrlWithProtocol(swipeUpLink)) {
      validationErrors = PojoSet.add(
        validationErrors,
        InstagramPostDetailsValidationError.InvalidUrl,
      );
    }
  }

  return validationErrors;
};

export const isValidIgStoryDuration = (
  videoDurationMillis: number,
): boolean => {
  return (
    videoDurationMillis >= STORY_MIN_VIDEO_DURATION_MILLIS &&
    videoDurationMillis <= STORY_MAX_VIDEO_DURATION_MILLIS
  );
};

export const isValidIgStoryVideoSize = (videoSizeBytes: number): boolean => {
  return videoSizeBytes <= STORY_MAX_VIDEO_SIZE;
};

export const isValidIgStoryImageSize = (imageSizeBytes: number): boolean => {
  return imageSizeBytes <= STORY_MAX_IMAGE_SIZE;
};

export function isRecommendedIgStoryAspectRatio(aspectRatio: number): boolean {
  return (
    aspectRatio.toFixed(2) === RECOMMENDED_IG_STORY_ASPECT_RATIO.toFixed(2)
  );
}

export interface IStoryGroupSwipeUpLinkData {
  clipIndex: number;
  url: string;
  cuid?: Cuid;
}

export const getSwipeUpLinkDataFromStoryClips = (
  storyClips: Array<{
    cuid?: Cuid;
    customizations: IInstagramStoryGroupClipCustomization[];
  }>,
): IStoryGroupSwipeUpLinkData[] => {
  return storyClips
    .map((clip, index) => {
      const swipeUpUrl = clip.customizations[0]?.value;
      if (swipeUpUrl) {
        return {
          clipIndex: index,
          url: swipeUpUrl,
          cuid: clip.cuid,
        };
      }
    })
    .filter(isDefined);
};

export const defaultStoryClip = {
  customizations: [
    {
      customizationType: InstagramStoryGroupClipCustomizationType.SwipeUpUrl,
    },
  ],
  validationErrors: PojoSet.empty(),
  errors: {},
};

export const isCuralateShortLink = (url: string): boolean => {
  return url.startsWith('https://cur.lt');
};
